import React, { useState } from 'react';
import { Search, Shield, CheckCircle, XCircle, Eye, Star, AlertCircle } from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { useUserStore } from '../../stores/userStore';
import { formatDate } from '../../utils/formatters';
import VerificationDetailsModal from '../../components/admin/VerificationDetailsModal';
import RejectionModal from '../../components/admin/RejectionModal';
import { notificationTriggers } from '../../services/notificationTriggers';

const AdminVerifications = () => {
  const { users, updateUser } = useUserStore();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('pending');
  const [selectedVerification, setSelectedVerification] = useState<{
    userId: string;
    type: 'id' | 'address';
  } | null>(null);
  const [showRejectionModal, setShowRejectionModal] = useState<{
    userId: string;
    type: 'id' | 'address';
  } | null>(null);
  const [alert, setAlert] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Group verifications by user
  const groupedVerifications = Object.values(users).reduce((acc, user) => {
    if (!user.verifications) return acc;

    const matchesSearch = user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const hasVerifications = user.verifications?.id || user.verifications?.address;
    const matchesFilter = filter === 'all' || 
                         (filter === 'pending' && (user.verifications?.id?.status === 'pending' || user.verifications?.address?.status === 'pending')) ||
                         (filter === 'verified' && (user.verifications?.id?.status === 'verified' || user.verifications?.address?.status === 'verified')) ||
                         (filter === 'rejected' && (user.verifications?.id?.status === 'rejected' || user.verifications?.address?.status === 'rejected'));

    if (matchesSearch && hasVerifications && matchesFilter) {
      const isHostVerification = user.verifications.id && user.verifications.address;
      acc.push({
        user,
        verificationLevel: isHostVerification ? 'host' : 'basic',
        verifications: {
          id: user.verifications.id,
          address: user.verifications.address
        }
      });
    }

    return acc;
  }, [] as Array<{
    user: any;
    verificationLevel: 'basic' | 'host';
    verifications: {
      id?: any;
      address?: any;
    };
  }>);

  const handleApprove = async (userId: string, type: 'id' | 'address') => {
    const user = users[userId];
    if (!user?.verifications) return;

    const updatedVerifications = {
      ...user.verifications,
      [type]: {
        ...user.verifications[type],
        status: 'verified',
        verifiedDate: new Date().toISOString()
      }
    };

    updateUser(user.email, {
      ...user,
      verifications: updatedVerifications
    });

    // Send notification
    await notificationTriggers.onVerificationApproved(userId, type);

    setAlert({
      type: 'success',
      message: `${type === 'id' ? 'ID' : 'Address'} verification approved successfully`
    });

    setTimeout(() => setAlert(null), 3000);
  };

  const handleReject = async (userId: string, type: 'id' | 'address', reason: string) => {
    const user = users[userId];
    if (!user?.verifications) return;

    const updatedVerifications = {
      ...user.verifications,
      [type]: {
        ...user.verifications[type],
        status: 'rejected',
        rejectionReason: reason,
        rejectedDate: new Date().toISOString()
      }
    };

    updateUser(user.email, {
      ...user,
      verifications: updatedVerifications
    });

    // Send notification
    await notificationTriggers.onVerificationRejected(userId, type, reason);

    setShowRejectionModal(null);
    setAlert({
      type: 'success',
      message: `${type === 'id' ? 'ID' : 'Address'} verification rejected`
    });

    setTimeout(() => setAlert(null), 3000);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'verified':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {alert && (
          <div className={`p-4 rounded-lg ${
            alert.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {alert.message}
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search verifications..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>
          </div>
          <div className="flex gap-4">
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="verified">Verified</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Verifications List */}
        <div className="space-y-6">
          {groupedVerifications.map(({ user, verificationLevel, verifications }) => (
            <div key={user.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* User Header */}
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
                      {user.photo ? (
                        <img
                          src={user.photo}
                          alt={user.fullName}
                          className="h-12 w-12 rounded-full object-cover"
                        />
                      ) : (
                        <Shield className="h-6 w-6 text-gray-500" />
                      )}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-gray-900">
                        {user.fullName || 'Unnamed User'}
                      </h3>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      verificationLevel === 'host' 
                        ? 'bg-purple-100 text-purple-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {verificationLevel === 'host' ? (
                        <div className="flex items-center">
                          <Star className="h-4 w-4 mr-1" />
                          Host Verification
                        </div>
                      ) : (
                        'Basic Verification'
                      )}
                    </span>
                  </div>
                </div>
              </div>

              {/* Verifications */}
              <div className="divide-y divide-gray-200">
                {/* ID Verification */}
                {verifications.id && (
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">ID Verification</h4>
                        <p className="text-sm text-gray-500">
                          Type: {verifications.id.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-sm text-gray-500">
                          Submitted: {formatDate(verifications.id.uploadDate)}
                        </p>
                        {verifications.id.rejectionReason && (
                          <p className="text-sm text-red-600 mt-1">
                            Reason: {verifications.id.rejectionReason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(verifications.id.status)}`}>
                          {verifications.id.status.charAt(0).toUpperCase() + verifications.id.status.slice(1)}
                        </span>
                        {verifications.id.status === 'pending' && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setSelectedVerification({ userId: user.id, type: 'id' })}
                              className="p-1.5 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
                            >
                              <Eye className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setShowRejectionModal({ userId: user.id, type: 'id' })}
                              className="p-1.5 text-red-600 hover:text-red-900 rounded-full hover:bg-red-50"
                            >
                              <XCircle className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleApprove(user.id, 'id')}
                              className="p-1.5 text-green-600 hover:text-green-900 rounded-full hover:bg-green-50"
                            >
                              <CheckCircle className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Address Verification */}
                {verifications.address && (
                  <div className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="text-sm font-medium text-gray-900">Address Verification</h4>
                        <p className="text-sm text-gray-500">
                          Type: {verifications.address.type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </p>
                        <p className="text-sm text-gray-500">
                          Submitted: {formatDate(verifications.address.uploadDate)}
                        </p>
                        {verifications.address.rejectionReason && (
                          <p className="text-sm text-red-600 mt-1">
                            Reason: {verifications.address.rejectionReason}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(verifications.address.status)}`}>
                          {verifications.address.status.charAt(0).toUpperCase() + verifications.address.status.slice(1)}
                        </span>
                        {verifications.address.status === 'pending' && (
                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => setSelectedVerification({ userId: user.id, type: 'address' })}
                              className="p-1.5 text-gray-600 hover:text-gray-900 rounded-full hover:bg-gray-100"
                            >
                              <Eye className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setShowRejectionModal({ userId: user.id, type: 'address' })}
                              className="p-1.5 text-red-600 hover:text-red-900 rounded-full hover:bg-red-50"
                            >
                              <XCircle className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => handleApprove(user.id, 'address')}
                              className="p-1.5 text-green-600 hover:text-green-900 rounded-full hover:bg-green-50"
                            >
                              <CheckCircle className="h-5 w-5" />
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}

          {groupedVerifications.length === 0 && (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center">
              <Shield className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Verifications Found</h3>
              <p className="text-gray-600">
                {filter === 'pending' 
                  ? 'There are no pending verifications to review.'
                  : 'No verifications match your search criteria.'}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Verification Details Modal */}
      {selectedVerification && (
        <VerificationDetailsModal
          userId={selectedVerification.userId}
          type={selectedVerification.type}
          onApprove={() => {
            handleApprove(selectedVerification.userId, selectedVerification.type);
            setSelectedVerification(null);
          }}
          onReject={() => {
            setShowRejectionModal(selectedVerification);
            setSelectedVerification(null);
          }}
          onClose={() => setSelectedVerification(null)}
        />
      )}

      {/* Rejection Modal */}
      {showRejectionModal && (
        <RejectionModal
          type={showRejectionModal.type}
          onConfirm={(reason) => handleReject(showRejectionModal.userId, showRejectionModal.type, reason)}
          onClose={() => setShowRejectionModal(null)}
        />
      )}
    </AdminLayout>
  );
};

export default AdminVerifications;